import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Image as ImageIcon, Play, ChevronRight } from 'lucide-react';
import { FbAd } from '@/services/facebook-ads.service';
import { FbCreative } from '@/services/facebook-ad-creatives.service';
import { formatMetaInsightsValue, formatMetaCount, formatMetaPercent } from '@/utils/currency';
import {
  StatusPill,
  ActionsDropdown,
  PlatformIcons,
  InlineErrorAlert,
} from './SharedComponents';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AdWithCreative extends FbAd {
  creative_data?: FbCreative;
}

interface AdCardProps {
  ad: AdWithCreative;
  adSetId: string;
  currency?: string;
  error?: string;
  isLoading?: boolean;
  /**
   * Which platforms this ad actually runs on (resolved from the parent ad
   * set's `targeting.publisher_platforms`). When omitted, the icons aren't
   * shown — better than lying with both-on by default.
   */
  hasFacebook?: boolean;
  hasInstagram?: boolean;
  onToggleStatus?: () => void;
  onArchive?: () => void;
  onDismissError?: () => void;
}

// ─── AdCard ──────────────────────────────────────────────────────────────────

const AdCard: React.FC<AdCardProps> = ({
  ad,
  adSetId,
  currency = '',
  error,
  isLoading,
  hasFacebook = false,
  hasInstagram = false,
  onToggleStatus,
  onArchive,
  onDismissError,
}) => {
  const nav = useNavigate();
  const status = ad.effective_status ?? ad.status;
  const ins = ad.insights?.data?.[0];

  const creative = ad.creative_data;
  // Prefer the ImageKit mirror — Meta's signed `thumbnail_url` / `image_url`
  // expire in a few hours and start 403-ing, leaving the operator with
  // blank tiles in the campaign listing. `imagekit_url` is populated by
  // the backend on first sync; fall back to the Meta URLs while we're
  // still in that initial window or if the mirror hasn't run yet.
  const displayImage = creative?.imagekit_url || creative?.thumbnail_url || creative?.image_url;
  const isVideo = creative?.media_kind === 'video';

  const goToAdSet = () => nav(`/facebook/ad-sets/${adSetId}`);

  const headline = creative?.headline?.trim();
  const ctaLabel = creative?.cta?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="min-w-[256px] max-w-[272px] flex-shrink-0">
      <div
        onClick={goToAdSet}
        className={clsx(
          'group cursor-pointer overflow-hidden rounded-xl border border-border bg-surface shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-soft',
          error && 'border-danger/40 ring-1 ring-danger/20',
        )}
      >
        {/* Header strip — pill, name, status, actions. Mirrors AdsetSection. */}
        <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-2">
          <span className="inline-flex items-center rounded-full border border-info/25 bg-info-soft px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-info">
            Ad
          </span>
          <h5 className="line-clamp-1 flex-1 text-[12px] font-semibold text-text-primary" title={ad.name}>
            {ad.name}
          </h5>
          <StatusPill status={status} size="small" />
          <ActionsDropdown
            entityType="ad"
            currentStatus={status}
            isLoading={isLoading}
            onView={goToAdSet}
            onToggleStatus={onToggleStatus}
            onArchive={onArchive}
          />
        </div>

        {/* Thumbnail */}
        <div className="relative h-[120px] overflow-hidden bg-surface-alt">
          {displayImage ? (
            <img
              src={displayImage}
              alt={ad.name}
              className="h-full w-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-text-muted">
              <ImageIcon size={22} />
            </div>
          )}

          {isVideo && (
            <div className="absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded-full bg-black/55 px-1.5 py-0.5 text-[9px] font-semibold text-white backdrop-blur-sm">
              <Play size={9} className="fill-white" />
              Video
            </div>
          )}

          {(hasFacebook || hasInstagram) && (
            <div className="absolute right-1.5 top-1.5">
              <PlatformIcons hasFacebook={hasFacebook} hasInstagram={hasInstagram} />
            </div>
          )}
        </div>

        {/* Creative preview — headline + CTA chip */}
        {(headline || ctaLabel) && (
          <div className="space-y-1.5 border-t border-border px-3 pt-2 pb-2">
            {headline && (
              <p className="line-clamp-1 text-[11.5px] font-medium text-text-secondary" title={headline}>
                {headline}
              </p>
            )}
            {ctaLabel && (
              <span className="inline-flex items-center gap-1 rounded-md bg-primary-soft px-2 py-0.5 text-[10px] font-semibold text-primary">
                {ctaLabel}
                <ChevronRight size={10} />
              </span>
            )}
          </div>
        )}

        {/* Stats — always visible, em-dashes when empty so the card height never collapses */}
        <div className="grid grid-cols-4 gap-1 border-t border-border px-3 py-2">
          <Stat label="Impr" value={formatMetaCount(ins?.impressions)} />
          <Stat label="Clicks" value={formatMetaCount(ins?.clicks)} />
          <Stat label="CTR" value={formatMetaPercent(ins?.ctr)} />
          <Stat label="Spend" value={formatMetaInsightsValue(ins?.spend, currency)} accent />
        </div>

        {error && (
          <div className="px-3 pb-2">
            <InlineErrorAlert message={error} onDismiss={onDismissError} />
          </div>
        )}
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; accent?: boolean }> = ({
  label,
  value,
  accent,
}) => {
  const empty = !value || value === '—' || value === '0';
  return (
    <div className="text-center">
      <div
        className={clsx(
          'truncate text-[11px] font-bold tabular-nums',
          empty ? 'text-text-muted' : accent ? 'text-primary' : 'text-text-primary',
        )}
        title={value}
      >
        {empty ? '—' : value}
      </div>
      <div className="text-[8px] uppercase tracking-wide text-text-muted">{label}</div>
    </div>
  );
};

export default AdCard;

// ─── AdRow (horizontal scroll) ───────────────────────────────────────────────

interface AdRowProps {
  ads: AdWithCreative[];
  adSetId: string;
  currency?: string;
  hasFacebook?: boolean;
  hasInstagram?: boolean;
  errors?: Record<string, string>;
  loadingIds?: Set<string>;
  onToggleStatus?: (ad: FbAd) => void;
  onArchive?: (ad: FbAd) => void;
  onDismissError?: (adId: string) => void;
  loading?: boolean;
}

export const AdRow: React.FC<AdRowProps> = ({
  ads,
  adSetId,
  currency = '',
  hasFacebook,
  hasInstagram,
  errors,
  loadingIds,
  onToggleStatus,
  onArchive,
  onDismissError,
  loading,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 0);
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [ads.length]);

  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-hidden">
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton h-[240px] min-w-[256px] rounded-xl" />
        ))}
      </div>
    );
  }

  if (ads.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-surface px-4 py-6 text-center text-xs text-text-muted">
        No ads in this ad set yet.
      </div>
    );
  }

  return (
    <div className="relative">
      {showLeftFade && (
        <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-12 bg-gradient-to-r from-surface-alt to-transparent" />
      )}
      {showRightFade && (
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-12 bg-gradient-to-l from-surface-alt to-transparent" />
      )}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-1"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {ads.map((ad) => (
          <AdCard
            key={ad.id}
            ad={ad}
            adSetId={adSetId}
            currency={currency}
            hasFacebook={hasFacebook}
            hasInstagram={hasInstagram}
            error={errors?.[ad.id]}
            isLoading={loadingIds?.has(ad.id)}
            onToggleStatus={() => onToggleStatus?.(ad)}
            onArchive={() => onArchive?.(ad)}
            onDismissError={() => onDismissError?.(ad.id)}
          />
        ))}
      </div>
    </div>
  );
};
